import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, type Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  normalizeAuthUser,
  type AuthSessionResponse,
  type AuthUser,
  type ChangePasswordDto,
  type RefreshResponse,
  type ForgotPasswordResponse,
  type UpdateMeDto,
  type VerifyAndPasswordResponse,
  type ResendVerificationResponse,
  type IdentifyResponse,
  type VerifyOtpResponse,
  type ResendOtpResponse,
  type CreatePasswordOutput,
} from './auth.types';

/**
 * HTTP-only API service for auth endpoints.
 *
 * Data Access layer — performs HTTP communication only.
 * No business logic, no state management.
 *
 * Endpoints:
 *   - POST /auth/login
 *   - POST /auth/refresh
 *   - GET  /auth/me
 *   - PATCH /auth/me
 *   - POST /auth/change-password
 *   - POST /auth/forgot-password
 *   - POST /auth/reset-password
 *   - POST /auth/logout
 */
@Injectable({ providedIn: 'root' })
export class AuthApi {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  login(
    email: string,
    password: string,
    type: 'staff' | 'socio'
  ): Observable<AuthSessionResponse> {
    return this.http
      .post<AuthSessionResponse>(`${this.apiUrl}/auth/login`, {
        email,
        password,
        type,
      })
      .pipe(map((res) => ({ ...res, user: normalizeAuthUser(res.user) })));
  }

  refresh(): Observable<RefreshResponse> {
    return this.http.post<RefreshResponse>(
      `${this.apiUrl}/auth/refresh`,
      {},
      { withCredentials: true }
    );
  }

  me(): Observable<AuthUser> {
    return this.http
      .get<AuthUser>(`${this.apiUrl}/auth/me`)
      .pipe(map((user) => normalizeAuthUser(user)));
  }

  updateMe(dto: UpdateMeDto): Observable<AuthUser> {
    return this.http
      .patch<AuthUser>(`${this.apiUrl}/auth/me`, dto)
      .pipe(map((user) => normalizeAuthUser(user)));
  }

  changePassword(dto: ChangePasswordDto): Observable<AuthSessionResponse> {
    return this.http
      .post<AuthSessionResponse>(`${this.apiUrl}/auth/change-password`, dto)
      .pipe(map((res) => ({ ...res, user: normalizeAuthUser(res.user) })));
  }

  forgotPassword(email: string): Observable<ForgotPasswordResponse> {
    return this.http.post<ForgotPasswordResponse>(
      `${this.apiUrl}/auth/forgot-password`,
      { email }
    );
  }

  resetPassword(
    token: string,
    newPassword: string
  ): Observable<AuthSessionResponse> {
    return this.http
      .post<AuthSessionResponse>(`${this.apiUrl}/auth/reset-password`, { token, newPassword })
      .pipe(map((res) => ({ ...res, user: normalizeAuthUser(res.user) })));
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/logout`, {});
  }

  verifyAndSetPassword(
    memberId: string,
    otp: string,
    password: string
  ): Observable<VerifyAndPasswordResponse> {
    return this.http.post<VerifyAndPasswordResponse>(
      `${this.apiUrl}/members/${memberId}/verify-and-set-password`,
      { otp, password }
    );
  }

  resendVerification(
    memberId: string
  ): Observable<ResendVerificationResponse> {
    return this.http.post<ResendVerificationResponse>(
      `${this.apiUrl}/members/${memberId}/resend-verification`,
      {}
    );
  }

  // ─── Progressive Auth (Phase 41) ─────────────────────────────────────

  /** Identify user by email — backend returns nextStep (PASSWORD or OTP). Always 200 (anti-enumeration). */
  identify(email: string): Observable<IdentifyResponse> {
    return this.http.post<IdentifyResponse>(
      `${this.apiUrl}/auth/user/identify`,
      { email }
    );
  }

  /** Verify 6-digit OTP — returns verificationToken for password creation */
  verifyOtp(memberId: string, otp: string): Observable<VerifyOtpResponse> {
    return this.http.post<VerifyOtpResponse>(
      `${this.apiUrl}/auth/user/otp/verify`,
      { memberId, otp }
    );
  }

  /** Resend OTP — returns new maskedDestination */
  resendOtp(memberId: string): Observable<ResendOtpResponse> {
    return this.http.post<ResendOtpResponse>(
      `${this.apiUrl}/auth/user/otp/resend`,
      { memberId }
    );
  }

  /** Create password after OTP verification — returns session (accessToken + user) */
  createPassword(
    verificationToken: string,
    password: string
  ): Observable<CreatePasswordOutput> {
    return this.http
      .post<CreatePasswordOutput>(`${this.apiUrl}/auth/user/password`, { verificationToken, password })
      .pipe(map((res) => ({ ...res, user: normalizeAuthUser(res.user) })));
  }
}
